/**
 * Result screen component for displaying simulation performance metrics and managing post-SIM actions.
 *
 * @module components/sim/takeSim/TakeSimResult
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect, useState, useCallback, useRef} from "react";
import PropTypes from "prop-types";
import {
    Grid,
    Button,
    Tooltip,
} from "@material-ui/core";
import Breadcrumbs from "@material-ui/core/Breadcrumbs";
import StyledLink from "../../common/StyledLink";
import CircularProgress from "@material-ui/core/CircularProgress";
import {connect} from "react-redux";
import {
    userFinalScoreResponse,
    viewResult,
    toggleResultButton,
    currentStatus,
    addAssessor,
    SaveAssessorNotes,
    getAssessorNotes,
    checkAllLevelAgentAudio,
    saveVideoStatus,
    snowflyIdUpdated,
    userResponseSuccess,
    notifyScreenRecordingVideoUpdated,
} from "../../../redux/sims/actions";
import {useLocation} from "react-router-dom";
import VideoLabelIcon from "@material-ui/icons/VideoLabel";
import DashboardPermissions from "../../../permissions/dashboard";
import {permissionCheck} from "../../../utils/permissionCheck";
import {combinedElapsedSeconds} from "../../../utils/time";
import {
    audioMerge,
    saveScreenShareVideo,
    fetchVideoDetails,
} from "../../../services/result.service";
import {useStyles} from "./TakeSimResult.js";
import ResultScreenAccuracies from './ResultScreenAccuracies.jsx';
import TypingAndSelection from './TypingAndSelection.jsx';
import moment from "moment/moment";
import SplitButton from "../../common/SplitButton.jsx";
import SaveIcon from "@material-ui/icons/Save";
import StarsIcon from "@material-ui/icons/Stars";
import Typography from "@material-ui/core/Typography";
import VideoLoadingPoster from '../../../assets/images/video_loading.gif';
import VideoPlayer from './VideoPlayer.jsx';
import AssessorNotes from "./AssessorNotes";
import CallTranscript from "./CallTranscript";
import useAlert from "../../common/UseAlert";

/**
 * Functional component which presents the user with the results of a SIM
 *  Level run, allows replaying the call audio or just the Agent's
 *  audios in sequence, retry the SIM Level run, or send it for review.
 *
 * @typedef {Function} TakeSimResult
 * @param {Object} params - Object containing configuration settings and callback
 *      functions for the simulation result.
 * @property {number} elapsedTime - The time elapsed in seconds during the
 *      simulation run.
 * @property {number} wrapSeconds - Time in seconds for wrap-up actions or
 *      processes within the simulation.
 * @property {Function} userFinalScoreResponse - Callback function that
 *      processes and sends the user's final simulation score.
 * @property {Function} toggleResultButton - Callback function to toggle the
 *      visibility or state of the result button in the UI.
 * @property {Function} viewResult - Callback function to fetch and display the
 *      results of the simulation.
 * @property {Object} userResponse - The response data related to the user's
 *      performance in the simulation.
 * @property {Object} userResult - The result object containing detailed
 *      information about the user and their simulation outcome.
 * @property {Function} setUserResult - Function to update the state of the
 *      user result.
 * @property {Array} caseValuesArray - Array of case values utilized during the
 *      simulation execution.
 * @property {Object} user - Object representing the user interacting with the
 *      simulation.
 * @property {boolean} isPost - Flag indicating if the current process is a
 *      POST operation.
 * @property {string} simId - The ID of the current simulation session.
 * @property {Function} currentStatus - Callback function to manage or reset
 *      the simulation's current status.
 * @property {string} assessor_notes - Notes or comments added by the assessor
 *      related to the simulation.
 * @property {Function} addAssessor - Function to handle adding assessor-related
 *      notes or actions.
 * @property {Function} SaveAssessorNotes - Callback to save assessor notes for
 *      the current simulation.
 * @property {Function} getAssessorNotes - Callback to retrieve assessor notes
 *      associated with the session.
 * @property {string} assessor_notes - Saved notes provided by the assessor for
 *      the simulation.
 * @property {Function} checkAllLevelAgentAudio - Function to verify audio
 *      recordings related to all levels of the simulation.
 * @property {boolean} videoRecordingEnabled - Flag indicating whether video
 *      recording is enabled in the simulation.
 * @property {string} videoUploadStatus - Status of the video upload process in
 *      the simulation.
 * @property {Function} saveVideoStatus - Function that saves or updates the
 *      status of video recordings.
 * @property {string} simProductType - The type of product or simulation being
 *      rendered.
 * @property {Object} config - Configuration parameters for managing the
 *      simulation settings or environment.
 * @property {boolean} snowflyIdUpdated - Indicates if the Snowfly ID has been
 *      updated as part of the simulation flow.
 * @property {Function} notifyScreenRecordingVideoUpdated - Notify function when
 *      screen recording video is updated.
 * @property {boolean} hasVideo - Flag indicating if a video is available for the
 *      simulation result.
 * @property {Function} setHasVideo - Function to update the state of the
 *      `hasVideo` flag.
 */
const TakeSimResult = ({
    elapsedTime,
    wrapSeconds,
    userFinalScoreResponse,
    toggleResultButton,
    viewResult,
    userResponse,
    userResult,
    setUserResult,
    caseValuesArray,
    user,
    isPost,
    simId,
    currentStatus,
    assessorNotes,
    addAssessor,
    SaveAssessorNotes,
    getAssessorNotes,
    assessor_notes,
    checkAllLevelAgentAudio,
    videoRecordingEnabled,
    videoUploadStatus,
    saveVideoStatus,
    simProductType,
    config,
    snowflyIdUpdated,
    notifyScreenRecordingVideoUpdated,
    hasVideo,
    setHasVideo,
}) => {
    const classes = useStyles();
    const location = useLocation();
    const {setAlert} = useAlert();
    const urlParams = new URLSearchParams(location.search);
    const assignmentId = urlParams.get("assignment_id");
    const [notes, setNotes] = React.useState("");
    const agentUserId = urlParams.get("user_id");
    const takenSimId = urlParams.get("sim_id");
    const reviewer = urlParams.get("reviewer");
    const [noteStatus, setNoteStatus] = React.useState("view");
    const [open, setOpen] = React.useState(false);
    const [video, setVideo] = React.useState(null);
    const [videoTitle, setVideoTitle] = React.useState('');
    const [videoDetailsFetched, setVideoDetailsFetched] = React.useState(false);
    const [videoIndex, setVideoIndex] = React.useState(-1);
    const [enableVideoBtn, setEnableVideoBtn] = React.useState(false);
    const [disablePolling, setDisablePolling] = React.useState(false);
    const [progress, setProgress] = useState(0);
    const [sendingToSnowfly, setSendingToSnowfly] = React.useState(false);
    const [lastFiveVideos, setLastFiveVideos] = React.useState([]);
    const videoPollTimer = useRef(null);
    const videoLoaded = useRef(false);
    const lastFiveVideosChanged = useRef(false);
    const videoChanged = useRef(false);
    const [initiallyPost, setInitiallyPost] = React.useState(isPost);
    const [hasAssessorNotes, setHasAssessorNotes] = React.useState(false);

    const videoPollTime = 30000;
    let simLevelId = urlParams.get("levelId");

    useEffect(() => {
        if (userResult && userResult.scripts && userResult.scripts.length) {
            setUserResult(userResult);
        }

        const videoInfo = userResult?.screen_share_video ??
            userResult?.screen_recording_urls ??
            userResult?.video_url ??
            null;
        if (videoInfo?.status === 'saved') {
            setVideo(null);
            setLastFiveVideos([]);
            setVideoDetailsFetched(true);
            setEnableVideoBtn(true);
            setProgress(100);
            setVideo(videoInfo);
            setVideoIndex(videoIndex);

            let lastFive = videoInfo?.last_five?.length > 5 ?
                videoInfo?.last_five?.slice(0, 5) :
                videoInfo?.last_five?.slice();
            setLastFiveVideos(lastFive);
            lastFiveVideosChanged.current = false;
            videoChanged.current = false;
        } else {
            getVideoDetails();
        }
    }, [userResult]);

    useEffect(() => {
        if (initiallyPost) {
            let lapsedTime = elapsedTime;
            let timeInSeconds = elapsedTime;
            if (
                userResponse.resumeLevel &&
                elapsedTime &&
                userResponse.elapsedTime
            ) {
                const totalSeconds = combinedElapsedSeconds(lapsedTime, userResponse.elapsedTime);
                timeInSeconds = totalSeconds;
                lapsedTime = totalSeconds;
            }
            const payload = {
                ...caseValuesArray,
                isPost: true,
                assignment_id: userResponse.assignment_id,
                elapsedTime: lapsedTime,
                totalSeconds: timeInSeconds,
                wrapSeconds: wrapSeconds,
            };
            if (elapsedTime) {
                userFinalScoreResponse(userResponse._id, payload);
            }
        } else {
            const payload = {
                simLevelId,
                simId: takenSimId ? takenSimId : simId,
                ...(assignmentId ? {assignmentId} : {}),
            };
            viewResult(payload, agentUserId);
        }

        return () => {
            currentStatus(0);
        };
    }, [
        userResponse,
        userFinalScoreResponse,
        caseValuesArray,
        viewResult,
        isPost,
        simId,
        simLevelId,
        assignmentId,
        takenSimId,
        agentUserId,
        elapsedTime,
        currentStatus,
        wrapSeconds
    ]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(
        () => {
            setInitiallyPost(isPost);
        },
        [isPost]
    );

    useEffect(() => {
        const canUpdateNotes = permissionCheck(
                DashboardPermissions.assessor_notes.update.task,
                DashboardPermissions.assessor_notes.update.type
            ),
            canCreateNotes = permissionCheck(
                DashboardPermissions.assessor_notes.create.task,
                DashboardPermissions.assessor_notes.create.type
            ),
            canViewNotes = permissionCheck(
                DashboardPermissions.assessor_notes.view.task,
                DashboardPermissions.assessor_notes.view.type
            ),
            canUpdateOrCreateNotes = canUpdateNotes || canCreateNotes;

        if (initiallyPost) {
            if (canUpdateOrCreateNotes) {
                setNoteStatus("add");
            } else if (canViewNotes) {
                setNoteStatus("view");
            }
        } else {
            if (canUpdateOrCreateNotes) {
                if (userResult._id) {
                    getAssessorNotes(userResult._id);
                }
                setNotes(assessor_notes);
                setNoteStatus(assessor_notes === "" ? "add" : "edit");
            } else if (canViewNotes) {
                if (userResult._id) {
                    getAssessorNotes(userResult._id);
                }
                setNotes(assessor_notes);
                setNoteStatus("view");
            }
        }
    }, [getAssessorNotes, userResult._id, assessor_notes, initiallyPost]);

    useEffect(() => {
        const simID = takenSimId ? takenSimId : simId;
        checkAllLevelAgentAudio(simID, assignmentId, agentUserId);
    }, [takenSimId, simId, checkAllLevelAgentAudio, agentUserId, assignmentId]);

    useEffect(() => {
        toggleResultButton(true);
        return () => {
            toggleResultButton(false);
            if(videoPollTimer.current) {
                clearInterval(videoPollTimer.current);
                videoPollTimer.current = null;
            }
        }
    });

    useEffect(
        () => {
            setHasAssessorNotes(!!assessorNotes);
        },
        [assessorNotes]
    );

    /**
     * A function that handles the closure of a specific operation or modal.
     * It performs two primary actions:
     * 1. Disables or deactivates an assessor by passing `false` to the
     *      `addAssessor` function.
     * 2. Resets the notes to a predefined state using the `setNotes` function,
     *      with the value of the `assessor_notes` variable.
     */
    const handleClose = () => {
        addAssessor(false);
        setNotes(assessor_notes);
    };

    /**
     * Closes the video preview and resets related states and flags.
     *
     * This function performs the following tasks:
     * - Sets the current video to null.
     * - Resets the video index to -1.
     * - Logs an error to the console if an exception occurs during execution.
     * - Updates the state to close the video player.
     * - Sets a flag to indicate that the video has not been loaded.
     * - Synchronizes the last viewed videos to maintain consistent state if any
     *      changes were made.
     * - Resets tracking flags for video changes after applying updates.
     */
    const handleClosePreview = () => {
        try {
            setVideo(null);
            setVideoIndex(-1);
            //setLastFiveVideos([]);
        } catch (err) {
            console.error('Error while closing video player: %o', err);
        }
        setOpen(false);
        videoLoaded.current = false;

        if (lastFiveVideosChanged.current) {
            if (lastFiveVideos) {
                setLastFiveVideos([...lastFiveVideos]);
            }
            lastFiveVideosChanged.current = false;
        }

        if (videoChanged.current) {
            setVideo(video);
            videoChanged.current = false;
        }
    };

    /**
     * Opens the preview for a video by determining the appropriate video source
     * from the user result. It sets the video index to 0, formats and sets the
     * video title based on the creation date, and opens the preview dialog.
     *
     * Determines the video source from the user result object, prioritizing:
     * 1. `screen_share_video`
     * 2. `screen_recording_urls`
     * 3. `video_url`
     *
     * If none of these are available, the videoInfo variable is set to null.
     *
     * Side effects:
     * - Resets the video index to 0.
     * - Sets the video title as a formatted string representing the creation date.
     * - Triggers the state to open the preview.
     */
    const openPreview = () => {
        const videoInfo = userResult?.screen_share_video ??
            userResult?.screen_recording_urls ??
            userResult?.video_url ??
            null;
        setVideoIndex(0);
        setVideoTitle(
            moment(videoInfo.created_on).format('MM-DD-YYYY HH:mm:ss A')
        );
        setOpen(true);
    };

    /**
     * Handles the event when a menu item is clicked.
     *
     * @param {number} index - The index of the selected menu item.
     * @param {Object} option - The object representing the selected menu item.
     * @param {string} option.text - The text or label associated with the
     *      selected menu item.
     */
    const handleMenuItemClick = (index, option) => {
        setVideoIndex(index);
        setVideoTitle(option.text);
        setOpen(true);
    };

    /**
     * Handles the action of adding assessor notes.
     *
     * This function creates a payload containing the assessor's notes and
     * invokes the SaveAssessorNotes function to save the notes for a specific
     * user result. After saving, it toggles the assessor state by calling
     * the addAssessor function.
     *
     * @function handleAdd
     * @returns {void}
     */
    const handleAdd = () => {
        const payload = {
            assessor_notes: notes,
        };
        SaveAssessorNotes(userResult._id, payload);
        addAssessor(false);
    };

    /**
     * Handles the edit action by compiling the provided notes into a payload object
     * and saving assessor notes for a specific user. It also updates the state
     * of the assessor editing process.
     *
     * @function
     * @name handleEdit
     */
    const handleEdit = () => {
        const payload = {
            assessor_notes: notes,
        };
        SaveAssessorNotes(userResult._id, payload, true);
        addAssessor(false);
    };

    /**
     * Handles the saving of a screen share video.
     *
     * This function is responsible for saving the details of a screen share video
     * based on user interaction. It differentiates between standard screen recordings
     * and recordings marked as a "best practice." The saved video details are updated
     * in the respective video object and related state variables are updated accordingly.
     *
     * @async
     * @param {Object} ev - The event object from the triggering action.
     * @param {string} ev.currentTarget.id - The ID of the triggering element,
     *      used to determine if the video should be marked as a best practice.
     * @returns {Promise<Object>} The result of the save operation containing video
     *      details such as save timestamp and optional best practice marker.
     */
    const handleSaveScreenShareVideo = async (ev) => {
        const isBestPractice = ev.currentTarget.id === 'bestPracticeScreenRecording';
        let item = lastFiveVideos && lastFiveVideos[videoIndex] ?
            lastFiveVideos[videoIndex] :
            null;

        let result = await saveScreenShareVideo(
            item?._id ?? video?._id,
            item?.urls[0] ?? video?.urls[0],
            isBestPractice
        );

        if (item) {
            item.saved_on = result.video_details.saved_on;
            if (isBestPractice) {
                item.marked_best_practice_on = result.video_details.marked_best_practice_on;
            }

            lastFiveVideos[videoIndex] = item;
            lastFiveVideosChanged.current = true;
        } else {
            video.saved_on = result.video_details.saved_on;
            if (isBestPractice) {
                video.marked_best_practice_on = result.video_details.marked_best_practice_on;
            }
            //setVideo(video);
            videoChanged.current = true;
        }

        return result;
    };

    /**
     * Handles the click event for downloading videos.
     *
     * This asynchronous function processes a click event triggered by the user for video downloads.
     * Depending on the `videoIndex`, it determines the appropriate video URLs to be used for the download.
     * For valid URLs, it opens the first URL in a new browser tab immediately and opens the
     * second URL (if available) in another tab after a delay of 1 second.
     *
     * @function
     * @param {Event} evt - The event object associated with the click action.
     * @returns {Promise<void>} A promise that resolves when the action is completed.
     */
    const handleVideoDownloadClick = async (evt) => {
        evt.preventDefault();
        let urls = video?.urls;
        switch (videoIndex) {
            case 0:
            case -1:
                break;
            default:
                urls = video?.last_five[videoIndex]?.urls;
        }

        if (urls?.length) {
            await window.open(urls[0], '_blank');
            setTimeout(
                () => {
                    window.open(urls[1], '_blank');
                },
                1000
            );
        }
    };

    /**
     * Retrieves and manages the process of fetching video details asynchronously.
     * This function ensures that video details are fetched and handled according to the video's status.
     *
     * - Stops further execution if the video is already loaded or no valid user ID is found.
     * - Clears any existing video polling timer to avoid duplicate intervals.
     * - Fetches video details and takes respective actions based on the video status:
     *   - If the video status is "draft" and the creation timestamp is null:
     *     - Marks video details as fetched and updates application state to reflect the draft state.
     *   - If the video status is "saved" and progress is not at 100%, updates the video and progress details:
     *     - Fetches and updates the corresponding video data.
     *     - Updates application state with the latest video information.
     *     - Refreshes the list of last five videos.
     *     - Prepares the user result with updated video properties.
     *     - Sends a notification if screen recording video changes are detected.
     *   - If the video status is "failed":
     *     - Displays an error message to notify that video processing failed.
     *   - For "pending" status, adjusts the state and schedules periodic re-fetching using polling.
     * - Displays alerts for any errors encountered during the fetching process.
     *
     * The function utilizes React hooks like `useCallback` to memoize itself, ensuring it is only re-created
     * when its dependencies change.
     *
     * Dependencies, validators, and state management used by this function:
     * - `userResponse`, `userResult`: Objects containing user data.
     * - `videoLoaded`, `videoPollTimer`, `videoPollTime`: State and timers to control the polling mechanism.
     * - `setAlert`, `setVideo`, `setProgress`, `setHasVideo`, `saveVideoStatus`, `setEnableVideoBtn`,
     *      `notifyScreenRecordingVideoUpdated`: Utility functions to update state and trigger events.
     * - `fetchVideoDetails`: Asynchronous function used to fetch video metadata from the server.
     */
    const getVideoDetails = useCallback(
        async () => {
            if (
                videoLoaded.current ||
                !(userResponse._id ?? userResult._id ?? null)
            ) {
                return;
            }

            if (videoPollTimer.current) {
                clearInterval(videoPollTimer.current);
                videoPollTimer.current = null;
            }

            try {
                const videoDetails = await fetchVideoDetails(
                    userResponse?._id ?? userResult?._id
                );
                if (!videoDetails || !videoDetails.data) {
                    setAlert('Could not fetch video details', 'error');
                } else if (
                    (videoDetails.data.video_status === "draft") &&
                    (
                        videoDetails.data.video_info &&
                        videoDetails.data.video_info.created_on === null
                    )
                ) {
                    setVideoDetailsFetched(true);
                    saveVideoStatus(false);
                    setEnableVideoBtn(false);
                    setVideo(null);
                    setVideoIndex(-1);
                    setProgress(100);
                    setHasVideo(false);
                } else if (
                    videoDetails.data.video_info &&
                    (videoDetails.data.video_status === "saved") &&
                    (progress !== 100)
                ) {
                    setVideoDetailsFetched(true);
                    saveVideoStatus(true);
                    setEnableVideoBtn(true);
                    setVideo(videoDetails.data.video_info);
                    setVideoIndex(0);
                    videoDetails.data.video_info.last_five.unshift({
                        ...videoDetails.data.video_info
                    });
                    let lastFive = videoDetails.data.video_info.last_five?.length > 5 ?
                        videoDetails.data.video_info.last_five?.slice(0, 5) :
                        videoDetails.data.video_info.last_five?.slice();
                    setLastFiveVideos(lastFive);
                    setProgress(100);
                    setInitiallyPost(false);

                    const newResult = {
                        ...userResult,
                        video_status: videoDetails.data.video_info.status,
                        video_url: videoDetails.data.video_info.video_url,
                        screen_share_video: videoDetails.data.video_info,
                        screen_share_videos: videoDetails.data.video_info,
                        screen_recording_urls: videoDetails.data.video_info
                    };

                    setUserResult(newResult);

                    if (videoPollTimer.current) {
                        clearInterval(videoPollTimer.current);
                        videoPollTimer.current = null;
                    }

                    if (!videoLoaded.current) {
                        notifyScreenRecordingVideoUpdated(videoDetails.data);
                        videoLoaded.current = true;
                        videoPollTimer.current = null;
                    }
                } else {
                    if (videoDetails.data.video_status === "failed") {
                        setAlert('Video processing failed', 'error');
                        return;
                    } else {
                        setHasVideo(videoDetails.data.video_status === 'pending');
                    }
                    if (videoDetails.data.video_info) {
                        videoPollTimer.current = setInterval(getVideoDetails, videoPollTime);
                    }
                }
            } catch (err) {
                setAlert(err.message, 'error');
            }
        },
        []
    );

    /**
     * Displays a message indicating that the video was successfully uploaded
     * and is currently being processed.
     *
     * Shows an informational message to the user, notifying them about the
     * successful upload of the video and the fact that the processing may take
     * some time. Encourages the user to return later if needed.
     *
     * @returns {JSX.Element} A message component consisting of styled text elements.
     */
    const showVideoProcessingMessage = () => {
        return (
            <>
                <Typography color="inherit">
                    Video was successfully uploaded and is now being processed.
                </Typography>
                <Typography color="inherit">
                    This may take some time, so feel free to come back later.
                </Typography>
            </>
        );
    };

    /**
     * A function that manages the rendering of a video preview user interface
     *      component based on various conditions.
     * This function handles scenarios such as video processing, screen
     *      recording, and deciding when and how to display
     * preview buttons or dropdown menus for accessing video-related content.
     *
     * The behavior of the function depends on several factors, including:
     * - If video information is available and the video is being processed.
     * - Whether the user has enabled video recording and whether the video
     *      details have been fetched.
     * - Conditions related to saved or draft video statuses.
     *
     * @function
     * @returns {JSX.Element|null} Returns a JSX element for displaying video
     *      preview actions or null if no preview actions are applicable.
     */
    const showVideoPreview = () => {
        const videoInfo = userResult?.screen_share_video ??
            userResult?.screen_recording_urls ??
            userResult?.video_url ??
            null;
        if ((hasVideo || userResult?.hasVideo) && !videoInfo?.urls?.length) {
            if (
                (hasVideo || userResult?.hasVideo) ||
                (videoInfo && videoInfo.urls && (userResult.video_status === 'draft'))
            ) {
                videoPollTimer.current = setInterval(getVideoDetails, videoPollTime);
                return (
                    <Tooltip
                        title={
                            videoDetailsFetched && videoUploadStatus !== null
                                ? ""
                                : showVideoProcessingMessage()
                        }
                        style={{fontSize: '1rem'}}
                        placement="bottom"
                    >
                        <span>
                            <Button
                                variant="contained"
                                className={classes.preview}
                                size="small"
                                onClick={openPreview}
                                disabled={true}
                                startIcon={<CircularProgress className={classes.previewLoader}/>}
                            >
                                <span>Processing Video</span>
                            </Button>
                        </span>
                    </Tooltip>
                );
            }
            return null;
        }

        if (initiallyPost && videoRecordingEnabled) {
            if (!videoDetailsFetched) {
                if (videoPollTimer.current) {
                    clearInterval(videoPollTimer.current);
                }
                videoPollTimer.current = setInterval(getVideoDetails, videoPollTime);
            }
            return (
                <Tooltip
                    title={
                        videoDetailsFetched && videoUploadStatus !== null
                            ? ""
                            : showVideoProcessingMessage()
                    }
                    style={{fontSize: '1rem'}}
                    placement="bottom"
                >
                    <span>
                        <Button
                            variant="contained"
                            className={classes.preview}
                            size="small"
                            onClick={openPreview}
                            disabled={!videoDetailsFetched && !enableVideoBtn}
                            startIcon={videoDetailsFetched && videoUploadStatus !== null ? (
                                <VideoLabelIcon/>
                            ) : (
                                <CircularProgress className={classes.previewLoader}/>
                            )}
                        >
                            {(!videoDetailsFetched && !enableVideoBtn) ? (
                                <span>Processing Video</span>
                            ) : (
                                <span>Preview Screen Recording</span>
                            )}
                        </Button>
                    </span>
                </Tooltip>
            );
        } else if (
            !initiallyPost &&
            (
                (videoInfo?.status === "saved") ||
                (userResult.video_status === "saved" && videoInfo) ||
                (
                    (userResult.screen_recording_urls?.status === "saved") &&
                    userResult.screen_recording_urls?.urls?.length
                )
            )
        ) {
            let menuItems = lastFiveVideos?.map((item, index) => {
                return {
                    url: item.urls[0],
                    disabled: false,
                    selected: index === videoIndex,
                    text: moment(item.created_on).format('MM-DD-YYYY HH:mm:ss A'),
                    data: item,
                    callback: buildVideoMenuItem
                }
            });
            return (
                <SplitButton
                    options={menuItems}
                    onItemSelected={handleMenuItemClick}
                    mainButtonIcon={<VideoLabelIcon/>}
                    onMainButtonClick={openPreview}
                    mainButtonText={'Preview Screen Recording'}
                ></SplitButton>
            );
        } else if (
            !initiallyPost &&
            !videoDetailsFetched &&
            (userResult?.video_status !== "saved") &&
            userResult?.screen_recording_urls?.urls?.length
        ) {
            return (
                <Tooltip
                    title={showVideoProcessingMessage()}
                    style={{fontSize: '1rem'}}
                    placement="bottom"
                >
                    <span>
                        <Button
                            variant="contained"
                            className={classes.preview}
                            size="small"
                            onClick={openPreview}
                            disabled={true}
                            startIcon={<CircularProgress className={classes.previewLoader}/>}
                        >
                            <span>Processing Video</span>
                        </Button>
                    </span>
                </Tooltip>
            );
        }

        return null;
    };

    /**
     * Asynchronously sends audio data to the Snowfly service, if the required conditions are met.
     *
     * This function checks the configuration and user-specific data to determine if audio data can be
     * sent to Snowfly. If the conditions are satisfied, it constructs the necessary payload and attempts
     * to send the data using the `audioMerge` function. The function handles updating the user result
     * with the Snowfly ID if the operation is successful.
     *
     * Key operations performed:
     * 1. Evaluates whether the audio data is eligible for submission to Snowfly based on application
     *    configuration and user-specific flags.
     * 2. Constructs a payload containing essential data such as simulation name, level details,
     *    simulation ID, and user information.
     * 3. Tries to send the payload to the Snowfly service using the `audioMerge` function.
     * 4. Logs errors to the console, in case of failure, when in development mode.
     * 5. Updates relevant application state variables to reflect the submission status.
     *
     * Internal functions and state variables used:
     * - `setSendingToSnowfly`: Updates the state to indicate whether the data is currently being sent.
     * - `setUserResult`: Updates the user result object, including the Snowfly ID if the operation is successful.
     * - `snowflyIdUpdated`: Invoked to handle successful updates of the Snowfly ID.
     *
     * @returns {Promise<void>} A promise that resolves once the operation is complete. Does not return
     * any specific result.
     *
     * @throws Will log an error to the console if the operation fails and the environment is in
     * development mode.
     */
    const sendAudioToSnowfly = async () => {
        const canSendToSnowfly = config.snowFlyAudioMerge &&
            (
                (userResult.level && userResult.level.send_to_snowfly) ||
                (userResult.level && userResult.level.send_to_snowfly) ||
                (userResult.send_to_snowfly)
            );

        if (canSendToSnowfly) {
            setSendingToSnowfly(true);
            const sendData = {
                simName: userResult?.simTitle ?? userResult.simTitle,
                levelName: userResult?.level?.name ?? userResult?.level.name,
                levelNum: (userResult?.level?.order ?? userResult?.level?.order ?? 0) + 1,
                simId: simId,
                responseId: userResult?._id ?? userResult?._id,
                simLevelId: simLevelId,
                user: user,
            };

            try {
                let result = await audioMerge(sendData);
                if (result && result.data && result.data.success) {
                    let userRes = {...userResult, snowfly_id: result.data.snowflyId};
                    userRes.snowfly_id = result.data.snowflyId;
                    setUserResult(userRes);
                    snowflyIdUpdated(result.data.snowflyId);
                }
            } catch (err) {
                if (window.IS_DEVELOPMENT) {
                    console.error('Failed Sending recording of this level to Snowfly = %o, %o', sendData, err);
                }
            }
            setSendingToSnowfly(false);
        }
    };

    /**
     * Constructs a menu item component for a video menu, dynamically rendering
     *      content based on the provided option and data states.
     *
     * @function
     * @param {Object} option - An object representing the menu option, containing
     *      text to be displayed.
     * @param {Object} data - An object providing additional state information for rendering.
     *                        It includes:
     *                        - `marked_best_practice_on` (boolean): Determines
     *                          whether the "best practice" indicator is displayed.
     *                        - `saved_on` (boolean): Determines whether the "saved"
     *                          indicator is displayed if the "best practice"
     *                          indicator is not active.
     * @returns {JSX.Element} A JSX element representing a menu item with conditional
     *      icons and text.
     */
    const buildVideoMenuItem = (option, data) => {
        if (data.marked_best_practice_on) {
            return (<><StarsIcon/>&nbsp;{option.text}</>);
        } else if (data.saved_on) {
            return (<><SaveIcon/>&nbsp;{option.text}</>);
        }
        return (
            <>
                <span style={{marginRight: '2em', display: 'inline-block'}}></span>
                &nbsp;{option.text}
            </>
        )
    };

    if (
        initiallyPost &&
        videoRecordingEnabled &&
        !disablePolling &&
        videoUploadStatus
    ) {
        if (videoUploadStatus) {
            videoPollTimer.current && clearInterval(videoPollTimer.current);
            videoPollTimer.current = setInterval(getVideoDetails, videoPollTime);
        }

        setDisablePolling(true);
    } else if (!initiallyPost && !videoDetailsFetched && (userResult.video_status === "pending")) {
        videoPollTimer.current && clearInterval(videoPollTimer.current);
        videoPollTimer.current = setInterval(getVideoDetails, videoPollTime);

        //setDisablePolling(true);
    }

    console.log('Rendering component TakeSimResult');
    if (!initiallyPost && !videoPollTimer.current) {
        videoPollTimer.current = setInterval(getVideoDetails, videoPollTime);
    }

    return (
        <>
            <Grid container={true}
                  style={{
                      marginBottom: "10px",
                      flexDirection: "row",
                      alignContent: "space-between",
                      flexShrink: "1"
                  }}
                  className={classes.flex}
            >
                <Grid item xs={5}>
                    {!takenSimId && (
                        <Breadcrumbs
                            aria-label="breadcrumb"
                            className={classes.mainBreadBlock}
                        >
                            <StyledLink to="/dashboard" className={classes.breadcrumbs}>
                                Dashboard
                            </StyledLink>
                        </Breadcrumbs>
                    )}
                </Grid>

                <Grid item xs={2}>

                </Grid>

                <Grid item xs={5} style={{
                    display: 'flex',
                    flexDirection: 'row-reverse',
                    alignContent: 'center',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '1em',
                    flex: '1',
                    flexWrap: 'wrap',
                    margin: '0 auto'
                }}>
                    {reviewer === "false" ? (
                        <StyledLink
                            color="inherit"
                            underline="always"
                            to={{
                                pathname: "/sims",
                                state: (
                                    location.state.assignment &&
                                    location.state.assignment.sim_id
                                ) ? {
                                    ...location.state.assignment,
                                    reviewer: false,
                                    type: location.state.type,
                                } : {
                                    sim_id: takenSimId || simId,
                                    reviewer: false,
                                    type: location.state.type,
                                },
                            }}
                        >
                            <Tooltip
                                title='Re-Take or go to next level'
                                placement="bottom"
                            >
                                <span>
                                    <Button
                                        variant="contained"
                                        className={classes.retake}
                                        disableElevation
                                        color="primary"
                                        size="small"
                                    >
                                        Re-take
                                    </Button>
                                </span>
                            </Tooltip>
                        </StyledLink>
                    ) : (
                        <StyledLink
                            color="inherit"
                            underline="always"
                            to={{
                                pathname: "/sims",
                                state:
                                    location.state.assignment && location.state.assignment.sim_id
                                        ? { ...location.state.assignment, reviewer: true }
                                        : {
                                            sim_id: takenSimId || simId,
                                            reviewer: true,
                                            assignmentId,
                                            agentUserId,
                                        },
                            }}
                        >
                            <Button
                                variant="contained"
                                className={classes.retake}
                                disableElevation
                                color="primary"
                                size="small"
                            >
                                {/* FIXME - Not sure if this should be "Re-take" instead*/}
                                {/* View Other Level Result */}
                                Re-take
                            </Button>
                        </StyledLink>
                    )
                    }

                    {showVideoPreview()}
                </Grid>
            </Grid>
            <VideoPlayer
                onClose={handleClosePreview}
                open={open}
                videoIndex={videoIndex}
                videoTitle={videoTitle}
                loadingPoster={VideoLoadingPoster}
                onSaveScreenShareVideo={handleSaveScreenShareVideo}
                onDownloadScreenShareVideo={handleVideoDownloadClick}
                videoDetailsFetched={videoDetailsFetched}
                classes={classes}
                setAlert={setAlert}
            />
            <AssessorNotes
                onClose={handleClose}
                assessorNotes={hasAssessorNotes}
                classes={classes}
                noteStatus={noteStatus}
                setNotes={setNotes}
                notes={notes}
                onAdd={handleAdd}
                onEdit={handleEdit}
            />
            {Object.keys(userResult).length > 0 ?
                (<Grid container spacing={4}>
                    <ResultScreenAccuracies
                        userResult={userResult}
                        setUserResult={setUserResult}
                        isPost={initiallyPost}
                        config={config}
                        sendAudioToSnowfly={sendAudioToSnowfly}
                        sendingToSnowfly={sendingToSnowfly}
                    />
                    <CallTranscript
                        classes={classes}
                        simProductType={simProductType}
                        userResult={userResult}
                    ></CallTranscript>
                    {!simProductType.includes('Voice Only') && <TypingAndSelection/>}
                </Grid>) :
                <div style={{textAlign: 'center', padding: '55px'}}>
                    <CircularProgress/>
                </div>}
        </>
    );
};

/**
 * PropTypes definitions for the TakeSimResult component.
 * These define the expected types for the props that are
 * passed to the component to ensure proper usage and to catch
 * potential bugs during development.
 */
TakeSimResult.propTypes = {
    elapsedTime: PropTypes.number,//string,
    totalSeconds: PropTypes.number,
    wrapSeconds: PropTypes.number,
    userFinalScoreResponse: PropTypes.func,
    toggleResultButton: PropTypes.func,
    viewResult: PropTypes.func,
    userResponse: PropTypes.object,
    userResult: PropTypes.object,
    user: PropTypes.object,
    caseValuesArray: PropTypes.object,
    isPost: PropTypes.bool,
    currentStatus: PropTypes.func,
    assessorNotes: PropTypes.string,
    addAssessor: PropTypes.func,
    SaveAssessorNotes: PropTypes.func,
    getAssessorNotes: PropTypes.func,
    assessor_notes: PropTypes.string,
    checkAllLevelAgentAudio: PropTypes.func,
    allAudioProcessed: PropTypes.bool,
    videoRecordingEnabled: PropTypes.bool,
    videoUploadStatus: PropTypes.bool,
    videoBlob: PropTypes.object,
    saveVideoStatus: PropTypes.func,
    simProductType: PropTypes.string,
    snowflyIdUpdated: PropTypes.func,
    notifyScreenRecordingVideoUpdated: PropTypes.func,
    setUserResult: PropTypes.func,
    simId: PropTypes.string,
    config: PropTypes.object,
    hasVideo: PropTypes.bool,
    setHasVideo: PropTypes.func,
};

/**
 * Maps the state from the Redux store to the properties for a React component.
 *
 * @param {Object} state - The global Redux store state.
 * @returns {Object} An object containing the mapped state properties:
 * - `elapsedTime`: The elapsed time of the simulation.
 * - `totalSeconds`: The total seconds of the simulation.
 * - `wrapSeconds`: The wrap-up seconds of the simulation.
 * - `userResponse`: The response provided by the user.
 * - `userResult`: The result associated with the user.
 * - `user`: Information about the logged-in user.
 * - `caseValuesArray`: An array of case values used in the simulation.
 * - `resultScreenInputData`: Input data displayed on the result screen.
 * - `simId`: The simulation ID extracted from the stats result, if available.
 * - `assessorNotes`: Notes provided by the assessor in the simulation.
 * - `assessor_notes`: Alias for `assessorNotes`, to ensure compatibility.
 * - `allAudioProcessed`: A flag indicating whether all audio processing is complete.
 * - `videoRecordingEnabled`: A flag indicating if video recording is enabled.
 * - `videoUploadStatus`: The status of the video upload process.
 * - `videoBlob`: The video Blob object containing the recorded video.
 * - `simProductType`: The type of simulation product currently in use.
 */
const mapStateToProps = (state) => ({
    elapsedTime: state.sim.elapsedTime,
    totalSeconds: state.sim.totalSeconds,
    wrapSeconds: state.sim.wrapSeconds,
    userResponse: state.sim.userResponse,
    userResult: state.sim.userResult,
    user: state.login.user,
    caseValuesArray: state.sim.caseValuesArray,
    resultScreenInputData: state.sim.resultScreenInputData,
    simId: state?.sim?.stats?.result?.sim_id,
    assessorNotes: state.sim.assessor_notes,
    assessor_notes: state.sim.assessor_notes,
    allAudioProcessed: state.sim.allAudioProcessed,
    videoRecordingEnabled: state.sim.videoRecordingEnabled,
    videoUploadStatus: state.sim.videoUploadStatus,
    videoBlob: state.sim.videoBlob,
    simProductType: state.sim.currentSimProductType,
});

/**
 * The mapDispatchToProps object acts as a mapping between action creators and the component's props.
 * It facilitates the dispatching of actions to the Redux store from within the component.
 *
 * Properties:
 * - userFinalScoreResponse: Dispatches an action for handling the user's final score response.
 * - viewResult: Dispatches an action to handle viewing the result.
 * - toggleResultButton: Dispatches an action to toggle the state of the result button.
 * - currentStatus: Dispatches an action related to updating or retrieving the current status.
 * - addAssessor: Dispatches an action to add an assessor.
 * - SaveAssessorNotes: Dispatches an action to save notes related to the assessor.
 * - getAssessorNotes: Dispatches an action to retrieve assessor notes.
 * - checkAllLevelAgentAudio: Dispatches an action to check audio files for all levels of agents.
 * - saveVideoStatus: Dispatches an action to save the video status of the user or session.
 * - snowflyIdUpdated: Dispatches an action for updating the snowfly ID of a user.
 * - userResponseSuccess: Dispatches an action for handling successful user response.
 * - notifyScreenRecordingVideoUpdated: Dispatches an action to notify when screen recording video is updated.
 */
const mapDispatchToProps = {
    userFinalScoreResponse: userFinalScoreResponse,
    viewResult,
    toggleResultButton,
    currentStatus,
    addAssessor,
    SaveAssessorNotes,
    getAssessorNotes,
    checkAllLevelAgentAudio,
    saveVideoStatus,
    snowflyIdUpdated,
    userResponseSuccess,
    notifyScreenRecordingVideoUpdated,
};

export default connect(mapStateToProps, mapDispatchToProps)(TakeSimResult);
